import { DirectionalHint, Stack } from "@fluentui/react";

import { StyledBadgeRibbon } from "./Badge";
import { PlanButton } from "./styles";

import { OffersToolTip } from "@/components/Shared/OffersToolTip/OffersToolTip";
import { Plan, PlanOfferItem } from "@/modules/Offers/types/PlansOffers.types";

interface PlanCardWithBadgeProps {
  plan: PlanOfferItem;
  selectedPlan?: Plan;
  selectedCarrier?: PlanOfferItem;
  setSelectedPlan?: (plan?: Plan) => void;
  setSelectedCarrier?: (carrier?: PlanOfferItem) => void;
  openModal: () => void;
  formType: "ENERGY" | "LINES" | "TAGS";
}

interface IBadgeOption {
  name: string;
  text: string;
  color: string;
}

const badgeOptions: IBadgeOption[] = [
  { name: "tim fixo", text: "PBX TIM", color: "#063b88" },
  { name: "vivo fixo", text: "PBX VIVO", color: "#8821ba" },
  { name: "claro fixo", text: "PBX CLARO", color: "#e20000" },
  { name: "pbx 55", text: "55PBX", color: "#448757" },
  { name: "12 meses", text: "12 meses", color: "#00ad56" },
  { name: "black friday 2024", text: "Black Friday", color: "#000" },
  { name: "mês da mulher 2025", text: "Mês da Mulher", color: "#FF1493" },
  { name: "tim controle", text: "Controle", color: "#000080" },
  { name: "vivo controle", text: "Controle", color: "#000080" },
  { name: "natal 2024", text: "Natal", color: "#b71c1c" },
  { name: "travel", text: "Travel", color: "#00ad56" },
  { name: "pré", text: "Pré-Pago", color: "#00ad56" },
  { name: "vivo ilimitado 40", text: "PARA VOCÊ!", color: "#650199" },
  { name: "tim ilimitado 75", text: "PARA VOCÊ!", color: "#063b88" }
];

const getBadgeProps = (planName: string) => {
  return (
    badgeOptions.find(option => planName.toLowerCase().includes(option.name)) ||
    null
  );
};

const sortPlansByDuration = (plans: Plan[]) => {
  const plansByDuration = {
    12: [],
    24: [],
    other: []
  };

  plans.forEach(plan => {
    const duration = plan?.offer?.fidelityDuration;

    if (duration === 12) {
      plansByDuration[12].push(plan);
    } else if (duration === 24) {
      plansByDuration[24].push(plan);
    } else {
      plansByDuration.other.push(plan);
    }
  });

  const sortedPlans: Plan[] = [];
  const maxLength = Math.max(
    plansByDuration[12].length,
    plansByDuration[24].length
  );

  for (let i = 0; i < maxLength; i++) {
    if (i < plansByDuration[12].length) {
      sortedPlans.push(plansByDuration[12][i]);
    }

    if (i < plansByDuration[24].length) {
      sortedPlans.push(plansByDuration[24][i]);
    }
  }

  return [...sortedPlans, ...plansByDuration.other];
};

export const PlanCardWithBadge = ({
  plan,
  selectedPlan,
  selectedCarrier,
  setSelectedPlan,
  setSelectedCarrier,
  openModal,
  formType
}: PlanCardWithBadgeProps) => {
  const isTagOffer = formType === "TAGS";

  const handleSetSelectedCarrier = (carrier: PlanOfferItem) => {
    if (selectedCarrier?.id !== carrier.id) {
      setSelectedCarrier?.(carrier);
    }
  };

  const handleSetSelectedPlan = (planItem: Plan, carrier: PlanOfferItem) => {
    if (planItem.id === selectedPlan?.id) {
      setSelectedCarrier?.(undefined);
      setSelectedPlan?.(undefined);

      return;
    }

    handleSetSelectedCarrier(carrier);
    setSelectedPlan?.(planItem);
  };

  const shouldRenderPlan = (planItem: Plan) => {
    /**
     * Planos TAG vêm da API sem o campo `data` preenchido.
     * Para TAGS, o botão é renderizado usando o nome do plano em vez de um valor de pacote de dados.
     */
    if (isTagOffer) {
      return true;
    }

    return Boolean(planItem?.data);
  };

  const getPlanTitle = (planItem: Plan) => {
    const isNonDataPlan = planItem.data === "0";

    if (isTagOffer || isNonDataPlan) {
      return planItem.name;
    }

    const baseData = `${planItem.data} GB`;
    const bonusData = planItem?.offer?.bonusData?.value;

    return bonusData > 0 ? `${baseData} + ${bonusData} GB` : baseData;
  };

  const renderPlanButton = (planItem: Plan) => {
    const handlePlanClick = () => {
      handleSetSelectedPlan(planItem, plan);
      openModal();
    };

    return (
      <PlanButton
        id={`button-plan-${planItem.id}`}
        minWidth="100px"
        width="100%"
        isActive={planItem.id === selectedPlan?.id}
        onClick={handlePlanClick}
      >
        {getPlanTitle(planItem)}
      </PlanButton>
    );
  };

  const renderPlanCard = (planItem: Plan, index: number) => {
    const badgeProps = getBadgeProps(planItem?.name);
    const tooltipPosition =
      index % 2 === 0
        ? DirectionalHint.leftCenter
        : DirectionalHint.rightCenter;

    const button = renderPlanButton(planItem);

    const tooltipButton = (
      <OffersToolTip plan={planItem} position={tooltipPosition}>
        {button}
      </OffersToolTip>
    );

    return (
      <div key={planItem.id} style={{ width: "40%" }}>
        {badgeProps ? (
          <StyledBadgeRibbon text={badgeProps.text} color={badgeProps.color}>
            {tooltipButton}
          </StyledBadgeRibbon>
        ) : (
          tooltipButton
        )}
      </div>
    );
  };

  const sortedPlans = sortPlansByDuration(plan.plans);
  const visiblePlans = sortedPlans.filter(shouldRenderPlan);

  return (
    <Stack
      horizontal
      tokens={{ childrenGap: 20, padding: "30px 15px" }}
      style={{ width: "100%" }}
      wrap
      horizontalAlign="center"
    >
      {visiblePlans.map((planItem, index) => renderPlanCard(planItem, index))}
    </Stack>
  );
};
